import { FeatherIcon } from '../../components/FeatherIcon'
import { useStats, useStatsT } from './statsContext'
import { TopFanCard, TopMessageCard, TopPostCard, TopStreamCard } from './Cards'
import { Delta, Empty, ErrorLine, Kv, Loading } from './StatsParts'
import { StatsChart } from './StatsChart'
import {
  capitalize,
  formatDate,
  int,
  isTodayActive,
  langOf,
  money,
  OVERVIEW_ROWS,
  overviewChartQuery,
  shiftMonth,
  streakCalendar,
  streakDays,
  streakQuery,
  type Raw,
} from './statisticsData'
import { combined, useStat } from './statsStore'

export function OverviewEarningsMain() {
  const { t, intl, lang } = useStatsT()
  const { store, range, pills, setPill } = useStats()
  const monthly = useStat(store, 'earnings/chart', { monthlyTotal: '1' })
  const chart = useStat(store, 'earnings/chart', overviewChartQuery(range))

  let allTime
  let months = null
  if (monthly.status === 'ok') {
    const list = (Array.isArray(monthly.data?.monthlyTotal) ? (monthly.data.monthlyTotal as Raw[]) : [])
      .slice()
      .sort((a, b) => Date.parse(String(b.date)) - Date.parse(String(a.date)))
    const total = list.reduce((sum, m) => sum + (Number(m.net) || 0), 0)
    allTime = (
      <div className="ofx-alltime">
        <span>{t('allTime')}</span>
        <strong>{money(total)}</strong>
      </div>
    )
    months = (
      <div className="ofx-months">
        {list.map((m) => {
          const label = new Date(String(m.date)).toLocaleDateString(intl, { month: 'long', year: 'numeric', timeZone: 'UTC' })
          const text = langOf(lang) === 'en' ? label.replace(' ', ', ') : capitalize(label)
          return (
            <div key={String(m.date)} className="ofx-month-row" title={`${t('l.gross')} ${money(m.gross)}`}>
              <span>{text}</span>
              <strong>{money(m.net)}</strong>
            </div>
          )
        })}
      </div>
    )
  } else if (monthly.status === 'error') {
    allTime = <ErrorLine message={monthly.error} />
  } else {
    allTime = <Loading />
  }

  let body
  if (chart.status === 'loading') body = <Loading />
  else if (chart.status === 'error') body = <ErrorLine message={chart.error} />
  else {
    const d = chart.data ?? {}
    const active = OVERVIEW_ROWS.find((r) => r.key === pills.overviewRow) ?? OVERVIEW_ROWS[0]
    body = (
      <>
        <StatsChart main={{ points: (d[active.key] as Raw | undefined)?.chartAmount, kind: 'money', label: t(`l.${active.label}`) }} />
        <div className="ofx-table-head ofx-cols-radio">
          <span>{t('l.earnings')}</span>
          <span className="num">{t('l.gross')}</span>
          <span className="num">{t('l.net')}</span>
        </div>
        <div role="radiogroup" aria-label={t('l.earnings')}>
          {OVERVIEW_ROWS.map((r) => {
            const item = (d[r.key] ?? {}) as Raw
            const on = r.key === active.key
            return (
              <label key={r.key} className={`ofx-radio-row ${on ? 'is-on' : ''}`}>
                <input type="radio" name="ofx-overview-row" checked={on} onChange={() => setPill('overviewRow', r.key)} />
                <span>{t(`l.${r.label}`)}</span>
                <span className="num ofx-muted">{money(item.gross)}</span>
                <span className="num">{money(item.total)}</span>
              </label>
            )
          })}
        </div>
      </>
    )
  }
  return (
    <>
      <div className="ofx-block">{allTime}</div>
      <div className="ofx-block">{body}</div>
      {months}
    </>
  )
}

export function HighlightsMain() {
  return (
    <div className="ofx-highlights">
      <TopPostCard />
      <TopMessageCard />
      <TopStreamCard />
      <TopFanCard />
    </div>
  )
}

export function StreakMain() {
  const { t } = useStatsT()
  const { store, pills, setPill, openedAt } = useStats()
  const current = useStat(store, 'streaks', streakQuery({ y: openedAt.getFullYear(), m: openedAt.getMonth() }, openedAt))
  const top = useStat(store, 'streaks/top')
  const today = useStat(store, 'users/me/stats/today-activity')
  const state = combined(current, top, today)
  if (state.loading) return <Loading />
  if (state.error) return <ErrorLine message={state.error} />
  const td = today.data ?? {}
  const todayActive = isTodayActive(td)
  const active = (Array.isArray(current.data?.list) ? (current.data.list as Raw[]) : []).find((s) => s?.isActive) ?? null
  const best = top.data && typeof top.data === 'object' && top.data.id ? top.data : null
  const currentDays = streakDays(active, todayActive)
  const bestDays = Math.max(streakDays(best, todayActive && best?.id === active?.id), currentDays)
  const tab = pills.streakTab
  const tip = t('todayActivity', { posts: td.posts || 0, stories: td.stories || 0, chats: td.chats || 0, streams: td.streams || 0 })
  return (
    <>
      <div className="ofx-streak-banner">
        <FeatherIcon name="zap" size={18} />
        <span>
          <strong>{t('takeAction')}</strong> {t('keepStreak')}
        </span>
        <span className="ofx-info-tip" tabIndex={0} title={tip} aria-label={tip}>
          <FeatherIcon name="info" size={14} />
        </span>
      </div>
      <div className="ofx-streak-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'current'} className={tab === 'current' ? 'is-on' : ''} onClick={() => setPill('streakTab', 'current')}>
          <strong className="is-hot">{int(currentDays)}</strong>
          <span>{t('currentStreak')}</span>
        </button>
        <button type="button" role="tab" aria-selected={tab === 'longest'} className={tab === 'longest' ? 'is-on' : ''} onClick={() => setPill('streakTab', 'longest')}>
          <strong>{int(bestDays)}</strong>
          <span>{t('longestStreak')}</span>
        </button>
      </div>
      {tab === 'longest' ? <LongestStreak best={best} bestDays={bestDays} todayActive={todayActive} /> : <StreakCalendar todayActive={todayActive} />}
    </>
  )
}

function StreakCalendar({ todayActive }: { todayActive: boolean }) {
  const { t, intl } = useStatsT()
  const { store, streakMonth, setStreakMonth, openedAt } = useStats()
  const entry = useStat(store, 'streaks', streakQuery(streakMonth, openedAt))
  const now = new Date()
  const isCurrent = streakMonth.y === now.getFullYear() && streakMonth.m === now.getMonth()
  const title = capitalize(new Date(streakMonth.y, streakMonth.m, 1).toLocaleDateString(intl, { month: 'long', year: 'numeric' }))
  const cells = streakCalendar(streakMonth, entry.data?.list, todayActive, now)
  const weekdays = Array.from({ length: 7 }, (_, i) => new Date(2026, 1, i + 1).toLocaleDateString(intl, { weekday: 'narrow' }))
  const go = (step: number) => {
    const next = shiftMonth(streakMonth, step, now)
    if (next) setStreakMonth(next)
  }
  return (
    <div className="ofx-block ofx-calendar">
      <div className="ofx-cal-head">
        <button type="button" className="lib-icon-btn" aria-label={t('prevMonth')} onClick={() => go(-1)}>
          <FeatherIcon name="chevron-left" size={18} />
        </button>
        <strong>{title}</strong>
        <button type="button" className="lib-icon-btn" aria-label={t('nextMonth')} disabled={isCurrent} onClick={() => go(1)}>
          <FeatherIcon name="chevron-right" size={18} />
        </button>
      </div>
      {entry.status === 'error' && <ErrorLine message={entry.error} />}
      <div className="ofx-cal-grid" aria-busy={entry.status === 'loading'}>
        {weekdays.map((w, i) => (
          <span key={`w${i}`} className="ofx-cal-cell is-wd">
            {w}
          </span>
        ))}
        {cells.map((c, i) => (
          <span key={i} className={`ofx-cal-cell ${c.on ? 'is-on' : ''} ${c.today ? 'is-today' : ''}`}>
            {c.day ?? ''}
          </span>
        ))}
      </div>
    </div>
  )
}

function LongestStreak({ best, bestDays, todayActive }: { best: Raw | null; bestDays: number; todayActive: boolean }) {
  const { t, intl } = useStatsT()
  const { store } = useStats()
  const earnings = useStat(store, 'users/me/stats/overview', { startDate: String(best?.startDate ?? ''), endDate: String(best?.endDate ?? ''), by: 'earnings' }, Boolean(best))
  if (!best) return <Empty text={t('empty.streak')} />
  const ongoing = Boolean(best.isActive) && todayActive
  const end = ongoing ? new Date() : new Date(String(best.endDate))
  const secs = Number(best.streamsDuration) || 0
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: '2-digit', year: 'numeric' }
  const rows: [string, string][] = [
    ['createdPosts', int(best.postsCount)],
    ['publishedStories', int(best.storiesCount)],
    ['replies', int(best.chatsCount)],
    ['liveTime', t('minSec', { m: Math.floor(secs / 60), s: secs % 60 })],
  ]
  const earning = (earnings.data?.earning ?? {}) as Raw
  return (
    <div className="ofx-block ofx-longest">
      <p>{t('longestText', { from: formatDate(best.startDate, intl, { ...opts, timeZone: 'UTC' }), to: formatDate(end, intl, opts), n: int(bestDays) })}</p>
      {rows.map(([k, v]) => (
        <Kv key={k} label={t(k)} value={v} />
      ))}
      <div className="ofx-kv is-total">
        <span>{t('totalEarnings')}</span>
        <span>
          {earnings.status === 'ok' ? (
            <>
              <strong>{money(earning.total)}</strong>
              <Delta value={earning.delta} />
            </>
          ) : earnings.status === 'error' ? (
            '—'
          ) : (
            '…'
          )}
        </span>
      </div>
    </div>
  )
}
